import {
  extractThumbnailPictureUrl,
  extractBestPictureUrl,
  extractPictures,
} from "./picturesExtractor";
import { extractInstrumentations } from "./instrumentationsExtractor";
import { parseISO8601Date } from "../utils/dateTimeUtils";
import { extractPrimaryValue, extractValue } from "./valuesExtractor";
import SpecificationKeys from "../constants/SpecificationKeys";
import { extractSystems } from "./systemsExtractor";

import { NodeDto, SpecificationDto } from "../types/api/v1/responses";

export type ExtractedValue = Readonly<{
  key: string;
  timestamp: Date;
  display_label: string;
  unit: string;
  value: number;
}> | null;

export type ExtractedAsset = Readonly<{
  assetProductionDate: string;
  lastSeenAt: number | null;
  manufacturerName: string;
  thumbnailUrl: string;
  productPictureUrl: string;
  pictures: string[];
  productTenantPublic: boolean;
  orderCode: string;
  sensorModuleBuildNumber: string;
  description: string;
  itemType: string;
  inSubscription: boolean;
  isMebakActivated: boolean;
  isConnectedAsset: boolean;
  lastStatusChangedAt: string;
  productId: number;
  productName: string;
  productCode: string;
  productStatusCode: string;
  productStatusName: string;
  assetStatusCode: string;
  assetStatusName: string;
  id: number;
  serialNumber: string;
  specifications: {
    [key: string]: SpecificationDto;
  };
  nodes?: NodeDto[];
  instrumentations: $TSFixMe;
  systems: $TSFixMe;
  value?: ExtractedValue;
  values: ExtractedValue[];
  product: $TSFixMe;
  productSpecifications?: {
    [key: string]: SpecificationDto;
  };
  additional_data_history_period?: number;
}>;

export function extractThumbnailAssetPictures(rawAsset: $TSFixMe) {
  return rawAsset.product?.pictures?.length > 0
    ? rawAsset.product.pictures
    : [];
}

function extractFromStatus(status: $TSFixMe) {
  return {
    assetStatusCode: status?.code || null,
    assetStatusName: status?.name || null,
  };
}

function extractFromProduct(product: $TSFixMe) {
  return {
    product,
    productId: product?.id,
    productName: product?.name,
    productCode: product?.product_code,
    productSpecifications: product?.specifications,
    productStatusCode: product?.status?.code || null,
    productStatusName: product?.status?.name || null,
  };
}

export function extractAsset(rawAsset: $TSFixMe): ExtractedAsset {
  const {
    id,
    serial_number: serialNumber,
    status,
    production_date: assetProductionDate,
    last_seen_at: lastSeenAt,
    product,
    specifications,
    nodes,
    description,
    instrumentations,
    systems,
    tenant,
    in_subscription: inSubscription = true,
    values,
    last_status_changed_at: lastStatusChangedAt,
    additional_data_history_period: additionalDataHistoryPeriod,
  } = rawAsset;

  const asset: ExtractedAsset = {
    id,
    serialNumber,
    ...extractFromStatus(status),
    ...extractFromProduct(product),
    assetProductionDate: assetProductionDate || null,
    lastSeenAt: lastSeenAt ? parseISO8601Date(lastSeenAt) : null,
    manufacturerName: product?.manufacturer?.name || null,
    thumbnailUrl: extractThumbnailPictureUrl(
      extractThumbnailAssetPictures(rawAsset),
    ),
    productPictureUrl: extractBestPictureUrl(product?.pictures),
    pictures: rawAsset.pictures ? extractPictures(rawAsset) : null,
    productTenantPublic: tenant == null || product?.tenant?.public || false,
    specifications,
    nodes: nodes?.items,
    orderCode: specifications?.["eh.pcps.tmp.ordercode"]?.value || null,
    sensorModuleBuildNumber:
      specifications?.[SpecificationKeys.GeneralSettings.SENSOR_BUILD_NUMBER]
        ?.value || null,
    description: description || null,
    instrumentations: instrumentations
      ? extractInstrumentations(instrumentations)
      : null,
    systems: systems ? extractSystems(systems) : null,
    itemType: "asset",
    inSubscription,
    value: extractPrimaryValue(values, false),
    isMebakActivated:
      systems?.items[0]?.specifications?.[
        SpecificationKeys.MebakActivatedSpecs.Mebak_Activated
      ]?.value === "true" || false,
    isConnectedAsset:
      !!product?.specifications?.[
        SpecificationKeys.ProductSpecs.CONNECTED_ASSET
      ],
    lastStatusChangedAt: lastStatusChangedAt || null,
    values: values ? values.map(extractValue) : [],
    additional_data_history_period: additionalDataHistoryPeriod,
  };

  return asset;
}

export function extractAssets(rawAssets: $TSFixMe) {
  return rawAssets?.assets.map(extractAsset);
}

export const extractLatLong = (asset: $TSFixMe) => [
  Number(asset?.specifications?.latitude?.value),
  Number(asset?.specifications?.longitude?.value),
];
